//src/Hooks/useMarkerIconsManage.js

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  collection,
  onSnapshot,
  doc,
  writeBatch,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../Firebase/firebase';

export function useMarkerIconsManage() {
  // --- STATE: UI ---
  const [activeAction, setActiveAction] = useState('VIEW');
  const [isProcessing, setIsProcessing] = useState(false);

  // --- STATE: LOADING (Race Condition Fix) ---
  const [iconsReady, setIconsReady] = useState(false);
  const [catsReady, setCatsReady] = useState(false);

  // --- STATE: SELECTION & FOCUS ---
  const [selectedIds, setSelectedIds] = useState([]);
  const [focusedRowId, setFocusedRowId] = useState(null);

  // --- STATE: DATA ---
  const [dbRows, setDbRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newRows, setNewRows] = useState([]);
  const [edits, setEdits] = useState({});

  // --- STATE: PENDING UPLOADS ---
  const [pendingUploads, setPendingUploads] = useState({});

  // --- STATE: ALERTS ---
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showUnsavedAlert, setShowUnsavedAlert] = useState(false);
  const [showDefaultDeleteAlert, setShowDefaultDeleteAlert] = useState(false);
  const [showArchiveDefaultAlert, setShowArchiveDefaultAlert] = useState(false); // AMENDED: New alert state
  const [showUsageBlockAlert, setShowUsageBlockAlert] = useState(false);
  const [usageCount, setUsageCount] = useState(0);

  // --- STATE: INTERNAL ---
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const fileInputRef = useRef(null);
  const uploadTargetIdRef = useRef(null);

  // --- 1. FETCH DATA (Icons AND Categories) ---
  useEffect(() => {
    const unsubIcons = onSnapshot(
      collection(db, 'markerIcons'),
      (snapshot) => {
        const fetchedData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            categoryId: data.categoryId || data.defaultCategoryId || '',
          };
        });
        setDbRows(fetchedData);
        setIconsReady(true);
      },
      (error) => {
        console.error('Error fetching marker icons:', error);
        setIconsReady(true);
      },
    );

    const unsubCats = onSnapshot(
      collection(db, 'categories'),
      (snapshot) => {
        const fetchedCats = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((cat) => !cat.isArchived);

        fetchedCats.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setCategories(fetchedCats);
        setCatsReady(true);
      },
      (error) => {
        console.error('Error fetching categories:', error);
        setCatsReady(true);
      },
    );

    return () => {
      unsubIcons();
      unsubCats();
    };
  }, []);

  // --- 2. DATA PROCESSING (Memoized) ---
  const displayRows = useMemo(() => {
    const mergedDbRows = dbRows.map((row) => {
      if (edits[row.id]) {
        return { ...row, ...edits[row.id] };
      }
      return row;
    });

    const newRowIds = new Set(newRows.map((r) => r.id));
    const finalDbRows = mergedDbRows.filter((r) => !newRowIds.has(r.id));

    // AMENDED: Prevent auto-sorting while actively editing a row to stop jumping
    if (!focusedRowId) {
      finalDbRows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else {
      finalDbRows.sort((a, b) => {
        const origA = dbRows.find((r) => r.id === a.id)?.name || '';
        const origB = dbRows.find((r) => r.id === b.id)?.name || '';
        return origA.localeCompare(origB);
      });
    }

    return [...finalDbRows, ...newRows];
  }, [dbRows, edits, newRows, focusedRowId]);

  const checkIsDirty = useCallback(() => {
    return newRows.length > 0 || Object.keys(edits).length > 0;
  }, [newRows, edits]);

  // --- 3. HELPER ACTIONS ---
  const resetInterface = () => {
    setActiveAction('VIEW');
    setSelectedIds([]);
    setFocusedRowId(null);
    setNewRows([]);
    setEdits({});
    setPendingUploads({});
    setShowUnsavedAlert(false);
    setShowDeleteAlert(false);
    setShowDefaultDeleteAlert(false);
    setShowArchiveDefaultAlert(false); // AMENDED
    setShowUsageBlockAlert(false);
    setPendingDeleteIds([]);
  };

  const handleCheckboxChange = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      }
      return [...prev, id];
    });
  };

  const handleFieldChange = (id, field, val) => {
    // AMENDED: Intercept and block attempts to archive the default marker
    if (field === 'isArchived' && val === true) {
      const targetRow = displayRows.find((r) => r.id === id);
      if (targetRow && targetRow.isDefault) {
        setShowArchiveDefaultAlert(true);
        return;
      }
    }

    if (activeAction === 'VIEW') setActiveAction('EDIT_MODE');

    const isNewRow = newRows.some((r) => r.id === id);
    if (isNewRow) {
      setNewRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, [field]: val } : row)),
      );
    } else {
      setEdits((prev) => ({
        ...prev,
        [id]: { ...(prev[id] || {}), [field]: val },
      }));
    }
  };

  const handleSetDefault = (targetId) => {
    if (activeAction === 'VIEW') setActiveAction('EDIT_MODE');
    setNewRows((prev) =>
      prev.map((row) => {
        if (row.id === targetId) return { ...row, isDefault: true, isArchived: false }; // AMENDED: Automatically un-archive if made default
        return { ...row, isDefault: false };
      }),
    );
    setEdits((prev) => {
      const newEdits = { ...prev };
      displayRows.forEach((row) => {
        if (!newRows.some((nr) => nr.id === row.id)) {
          if (row.id === targetId) {
            newEdits[row.id] = {
              ...(newEdits[row.id] || {}),
              isDefault: true,
              isArchived: false,
            }; // AMENDED: Automatically un-archive if made default
          } else if (row.isDefault) {
            newEdits[row.id] = { ...(newEdits[row.id] || {}), isDefault: false };
          }
        }
      });
      return newEdits;
    });
  };

  // --- 4. FILE UPLOAD (DEFERRED) ---
  const handleIconCellClick = (id) => {
    uploadTargetIdRef.current = id;
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsProcessing(true);

    // Enforce minimum delay to ensure React commits the state and paints the spinner
    await new Promise((res) => setTimeout(res, 500));

    const targetId = uploadTargetIdRef.current;
    if (!targetId) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      setIsProcessing(false);
      return;
    }

    const previewUrl = URL.createObjectURL(file);

    setPendingUploads((prev) => ({
      ...prev,
      [targetId]: file,
    }));

    handleFieldChange(targetId, 'icon', previewUrl);

    if (fileInputRef.current) fileInputRef.current.value = '';
    uploadTargetIdRef.current = null;

    setIsProcessing(false);
  };

  // --- 5. CORE ACTIONS ---
  const handleAddNew = () => {
    const newId = Date.now().toString();
    const newRow = {
      id: newId,
      name: '',
      icon: '',
      isDefault: false,
      isArchived: false,
      categoryId: '',
    };
    setNewRows((prev) => [...prev, newRow]);
    setActiveAction('EDIT_MODE');
    setFocusedRowId(newId);
  };

  // --- SAVE LOGIC (With Orphan Check) ---
  const handleSave = async () => {
    if (!checkIsDirty()) return;

    // AMENDED: Safety validation before permitting save
    const currentDefault = displayRows.find((r) => r.isDefault);
    if (currentDefault && currentDefault.isArchived) {
      setShowArchiveDefaultAlert(true);
      return;
    }

    setIsProcessing(true);

    const startTime = Date.now();
    const filesToOrphanDelete = [];

    const resolveIconUrl = async (id, currentIconUrl) => {
      if (pendingUploads[id]) {
        try {
          const file = pendingUploads[id];
          const storageRef = ref(storage, `marker_icons/${Date.now()}_${file.name}`);
          const snapshot = await uploadBytes(storageRef, file);
          const downloadURL = await getDownloadURL(snapshot.ref);
          return downloadURL;
        } catch (error) {
          console.error(`Error uploading file for ${id}:`, error);
          return currentIconUrl;
        }
      }
      return currentIconUrl;
    };

    try {
      const batch = writeBatch(db);

      // A. Process New Rows
      const processedNewRows = await Promise.all(
        newRows.map(async (row) => {
          const finalIconUrl = await resolveIconUrl(row.id, row.icon);
          return { ...row, icon: finalIconUrl };
        }),
      );

      processedNewRows.forEach((row) => {
        const docRef = doc(db, 'markerIcons', row.id);
        batch.set(docRef, {
          name: row.name,
          icon: row.icon || '',
          isDefault: row.isDefault || false,
          isArchived: row.isArchived || false,
          categoryId: row.categoryId || '',
        });
      });

      // B. Process Edits
      const editIds = Object.keys(edits);
      await Promise.all(
        editIds.map(async (id) => {
          const rawChanges = edits[id];
          let changesToSave = { ...rawChanges };

          if (rawChanges.icon) {
            const oldRow = dbRows.find((r) => r.id === id);
            const oldUrl = oldRow ? oldRow.icon : null;

            const finalIconUrl = await resolveIconUrl(id, rawChanges.icon);
            changesToSave.icon = finalIconUrl;

            if (oldUrl && oldUrl !== finalIconUrl && oldUrl.startsWith('http')) {
              const isUsedInDb = dbRows.some((r) => r.id !== id && r.icon === oldUrl);
              const isUsedInNew = newRows.some((r) => r.icon === oldUrl);

              if (!isUsedInDb && !isUsedInNew) {
                filesToOrphanDelete.push(oldUrl);
              }
            }
          }

          const docRef = doc(db, 'markerIcons', id);
          batch.update(docRef, changesToSave);
        }),
      );

      await batch.commit();

      if (filesToOrphanDelete.length > 0) {
        filesToOrphanDelete.forEach((url) => {
          const fileRef = ref(storage, url);
          deleteObject(fileRef).catch((err) =>
            console.warn(`[loKtr] Failed to delete orphaned file ${url}:`, err),
          );
        });
      }

      resetInterface();
    } catch (error) {
      console.error('Error saving batch:', error);
    } finally {
      const elapsed = Date.now() - startTime;
      const minDelay = 500;
      if (elapsed < minDelay) {
        await new Promise((res) => setTimeout(res, minDelay - elapsed));
      }

      setIsProcessing(false);
    }
  };

  const prepareDelete = async () => {
    let targets = [...selectedIds];
    if (targets.length === 0 && focusedRowId) targets = [focusedRowId];
    if (targets.length === 0) return;

    const isDeletingDefault = displayRows.some(
      (row) => targets.includes(row.id) && row.isDefault,
    );
    if (isDeletingDefault) {
      setShowDefaultDeleteAlert(true);
      return;
    }

    const localIdsToDelete = [];
    const dbIdsToDelete = [];

    targets.forEach((id) => {
      if (newRows.some((r) => r.id === id)) localIdsToDelete.push(id);
      else dbIdsToDelete.push(id);
    });

    if (localIdsToDelete.length > 0) {
      setPendingUploads((prev) => {
        const copy = { ...prev };
        localIdsToDelete.forEach((id) => delete copy[id]);
        return copy;
      });

      setNewRows((prev) => prev.filter((r) => !localIdsToDelete.includes(r.id)));
      setSelectedIds((prev) => prev.filter((id) => !localIdsToDelete.includes(id)));
    }

    if (dbIdsToDelete.length > 0) {
      try {
        const markersRef = collection(db, 'markers');
        let totalUsage = 0;

        const chunkSize = 10;
        for (let i = 0; i < dbIdsToDelete.length; i += chunkSize) {
          const chunk = dbIdsToDelete.slice(i, i + chunkSize);
          const q = query(markersRef, where('iconID', 'in', chunk));
          const snapshot = await getDocs(q);
          totalUsage += snapshot.size;
        }

        setPendingDeleteIds(dbIdsToDelete);
        if (totalUsage > 0) {
          setUsageCount(totalUsage);
          setShowUsageBlockAlert(true);
        } else {
          setShowDeleteAlert(true);
        }
      } catch (err) {
        console.error('Error checking usage:', err);
        setPendingDeleteIds(dbIdsToDelete);
        setShowDeleteAlert(true);
      }
    } else {
      setFocusedRowId(null);
    }
  };

  const executeDelete = async () => {
    if (pendingDeleteIds.length === 0) return;

    setIsProcessing(true);

    const startTime = Date.now();
    const batch = writeBatch(db);
    const storageDeletePromises = [];

    pendingDeleteIds.forEach((id) => {
      batch.delete(doc(db, 'markerIcons', id));

      const rowData = dbRows.find((r) => r.id === id);

      if (pendingUploads[id]) {
        // Skipping storage delete, pending upload detected
      } else if (rowData && rowData.icon && rowData.icon.startsWith('http')) {
        const isUsedElsewhere = dbRows.some(
          (r) => r.id !== id && r.icon === rowData.icon,
        );

        if (!isUsedElsewhere) {
          const imageRef = ref(storage, rowData.icon);
          storageDeletePromises.push(
            deleteObject(imageRef).catch((err) => {
              console.warn(`[loKtr] Failed to delete storage image for ${id}:`, err);
            }),
          );
        }
      }
    });

    try {
      if (storageDeletePromises.length > 0) {
        await Promise.all(storageDeletePromises);
      }
      await batch.commit();

      setEdits((prev) => {
        const copy = { ...prev };
        pendingDeleteIds.forEach((id) => delete copy[id]);
        return copy;
      });
      setPendingUploads((prev) => {
        const copy = { ...prev };
        pendingDeleteIds.forEach((id) => delete copy[id]);
        return copy;
      });

      setSelectedIds((prev) => prev.filter((id) => !pendingDeleteIds.includes(id)));
      setPendingDeleteIds([]);
      setFocusedRowId(null);
      setShowDeleteAlert(false);
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      const elapsed = Date.now() - startTime;
      const minDelay = 500;
      if (elapsed < minDelay) {
        await new Promise((res) => setTimeout(res, minDelay - elapsed));
      }
      setIsProcessing(false);
    }
  };

  const handleHeaderAction = (action) => {
    switch (action) {
      case 'ADD_NEW':
        return handleAddNew();
      // AMENDED: Removed UPLOAD case entirely
      case 'SAVE':
        return handleSave();
      case 'DELETE':
        return prepareDelete();
      case 'CLOSE':
        if (checkIsDirty()) setShowUnsavedAlert(true);
        else resetInterface();
        return;
      default:
        return;
    }
  };

  return {
    state: {
      loading: !iconsReady || !catsReady,
      isProcessing,
      displayRows,
      categories,
      selectedIds,
      focusedRowId,
      newRows,
      fileInputRef,
    },
    flags: {
      isDirty: checkIsDirty(),
      canSave: checkIsDirty(),
      canDelete: selectedIds.length > 0 || focusedRowId !== null,
      canClose: checkIsDirty(),
    },
    alerts: {
      showDelete: showDeleteAlert,
      setShowDelete: setShowDeleteAlert,
      showUnsaved: showUnsavedAlert,
      setShowUnsaved: setShowUnsavedAlert,
      showDefaultDelete: showDefaultDeleteAlert,
      setShowDefaultDelete: setShowDefaultDeleteAlert,
      showArchiveDefault: showArchiveDefaultAlert, // AMENDED
      setShowArchiveDefault: setShowArchiveDefaultAlert, // AMENDED
      showUsageBlock: showUsageBlockAlert,
      setShowUsageBlock: setShowUsageBlockAlert,
    },
    usage: { count: usageCount },
    actions: {
      handleHeaderAction,
      handleCheckboxChange,
      handleFieldChange,
      handleSetDefault,
      handleIconCellClick,
      handleFileChange,
      handleConfirmDelete: executeDelete,
      handleCloseConfirmed: resetInterface,
      setFocusedRowId,
    },
  };
}
